/**
 * Certification badges — admin-configurable marketing labels.
 * Max 2 per certification. Keys are persisted; labels are display-only.
 * Adding a new badge here also requires updating the DB CHECK constraint
 * on `certifications.badges`.
 */
export type BadgeKey = "most_popular" | "prerequisite" | "new";

export const BADGE_PRESETS: { key: BadgeKey; label: string }[] = [
  { key: "most_popular", label: "Most Popular" },
  { key: "prerequisite", label: "Prerequisite" },
  { key: "new", label: "New" },
];

const BADGE_LABEL_MAP: Record<BadgeKey, string> = Object.fromEntries(
  BADGE_PRESETS.map((b) => [b.key, b.label]),
) as Record<BadgeKey, string>;

export function isBadgeKey(value: unknown): value is BadgeKey {
  return typeof value === "string" && value in BADGE_LABEL_MAP;
}

/** Returns sanitized display label for a known badge key, or null. */
export function badgeLabel(key: string): string | null {
  return isBadgeKey(key) ? BADGE_LABEL_MAP[key] : null;
}

/** Filter, dedupe and cap to 2 valid badge keys. */
export function sanitizeBadges(input: unknown): BadgeKey[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<BadgeKey>();
  for (const v of input) {
    if (isBadgeKey(v)) seen.add(v);
    if (seen.size >= 2) break;
  }
  return Array.from(seen);
}